import React, { ReactElement, useEffect } from 'react';
import { Button, ButtonGroup, Dropdown, Tooltip, useStyles2 } from '@grafana/ui';
import { css, cx } from '@emotion/css';
import { ChatContextItem } from '../../context/types';
import { useAssistant } from '../../hook';
import { GrafanaTheme2 } from '@grafana/data';
import { OpenAssistantProps } from '../../sidebar';
import { reportInteraction } from '@grafana/runtime';

export interface OpenAssistantSplitButtonProps {
  /** Prompt to pass to the openAssistant function. */
  prompt: string;
  /** Origin of the request that opened the assistant. */
  origin: string;
  /** Context to pass to the openAssistant function. Optional, defaults to undefined. */
  context?: ChatContextItem[];
  /** Whether to automatically send the prompt. Optional, defaults to true. */
  autoSend?: boolean;
  /** Text to display on the button. Optional, defaults to 'Chat with Assistant' */
  title?: string;
  /** Button size, defaults to sm */
  size?: 'sm' | 'md' | 'lg';
  /** Function to call when the main button is clicked. Optional. */
  onClick?: () => void;
  /** Additional menu items to show in the dropdown. Optional. */
  menuItems?: ReactElement;
  /** Tooltip text to display on hover. Optional. */
  tooltip?: string;
  /** Width for the dropdown/chevron segment. Accepts pixels as number or any CSS width value. */
  dropdownButtonWidth?: number | string;
}

type AssistantButtonSize = NonNullable<OpenAssistantSplitButtonProps['size']>;

const defaultDropdownButtonWidths: Record<AssistantButtonSize, number> = {
  sm: 32,
  md: 36,
  lg: 40,
};

/**
 * A split button component that opens the Grafana Assistant with a dropdown for additional actions.
 * The gradient border wraps around both buttons as a unified control.
 */
export function OpenAssistantSplitButton(props: OpenAssistantSplitButtonProps) {
  const { isAvailable, openAssistant } = useAssistant();
  if (!isAvailable || !openAssistant) {
    return null;
  }
  return <OpenAssistantSplitButtonView {...props} openAssistant={openAssistant} />;
}

export function OpenAssistantSplitButtonView({
  prompt,
  origin,
  context,
  autoSend = true,
  title = 'Chat with Assistant',
  size = 'sm',
  onClick,
  menuItems,
  tooltip,
  dropdownButtonWidth,
  openAssistant,
}: OpenAssistantSplitButtonProps & {
  openAssistant: (props: OpenAssistantProps) => void;
}) {
  const styles = useStyles2(getStyles);
  const hasMenuItems = Boolean(menuItems);
  const resolvedDropdownButtonWidth = toCssWidth(dropdownButtonWidth ?? defaultDropdownButtonWidths[size]);

  useEffect(() => {
    reportInteraction('grafana_assistant_app_open_sidebar_split_button_displayed', {
      from: origin,
    });
  }, [origin]);

  const handleMainClick = () => {
    onClick?.();
    openAssistant({ prompt, context, autoSend, origin });
  };

  const wrapperContent = (
    <div className={styles.wrapper}>
      <ButtonGroup className={styles.buttonGroup}>
        <Button
          icon="ai-sparkle"
          onClick={handleMainClick}
          variant="secondary"
          fill="solid"
          size={size}
          aria-label={title}
          className={cx(styles.segmentButton, hasMenuItems ? styles.mainButtonSplit : styles.mainButtonSolo)}
          data-testid="assistant-split-button-main"
        >
          {title}
        </Button>
        {menuItems && (
          <Dropdown overlay={menuItems}>
            <Button
              variant="secondary"
              icon="angle-down"
              size={size}
              aria-label="More AI actions"
              className={cx(styles.segmentButton, styles.dropdownButton)}
              style={{ width: resolvedDropdownButtonWidth, minWidth: resolvedDropdownButtonWidth }}
              data-testid="assistant-split-button-dropdown"
            />
          </Dropdown>
        )}
      </ButtonGroup>
    </div>
  );

  return tooltip ? <Tooltip content={tooltip}>{wrapperContent}</Tooltip> : wrapperContent;
}

const getStyles = (theme: GrafanaTheme2) => {
  const baseBackground = theme.colors.secondary.main;
  const elevatedBackground = theme.colors.emphasize(baseBackground, 0.05);
  const underlyingColor = theme.colors.background.canvas;
  const dividerColor = theme.colors.border.weak;
  const borderWidth = 1;
  const outerRadius = theme.shape.radius.default;
  const innerRadius = `max(calc(${outerRadius} - ${borderWidth}px), 1px)`;

  return {
    wrapper: css({
      label: 'assistant-split-button-wrapper',
      position: 'relative',
      isolation: 'isolate',
      display: 'inline-block',
      // Gradient border around the entire button group
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: outerRadius,
        cornerShape: 'squircle',
        background: 'linear-gradient(90deg, rgb(168, 85, 247), rgb(249, 115, 22))',
        zIndex: -1,
        pointerEvents: 'none',
      },
      // Solid background layer
      '&::after': {
        content: '""',
        position: 'absolute',
        inset: `${borderWidth}px`,
        borderRadius: innerRadius,
        cornerShape: 'squircle',
        background: `linear-gradient(${baseBackground}, ${baseBackground}), ${underlyingColor}`,
        zIndex: -1,
        pointerEvents: 'none',
      },
    }),
    buttonGroup: css({
      label: 'assistant-split-button-group',
      gap: 0,
      position: 'relative',
      zIndex: 1,
      borderRadius: innerRadius,
      overflow: 'hidden',
      // Make buttons transparent to show the wrapper's gradient
      '& button': {
        border: 'none',
        boxShadow: 'none',
        background: 'transparent !important',
      },
    }),
    segmentButton: css({
      label: 'assistant-split-button-segment',
      position: 'relative',
      '&:hover': {
        background: `${elevatedBackground} !important`,
      },
      '&:focus-visible': {
        background: `${elevatedBackground} !important`,
      },
      '&[aria-expanded="true"]': {
        background: `${elevatedBackground} !important`,
      },
    }),
    mainButtonSolo: css({
      label: 'assistant-split-button-main-solo',
      borderRadius: innerRadius,
    }),
    mainButtonSplit: css({
      label: 'assistant-split-button-main-split',
      borderTopLeftRadius: innerRadius,
      borderBottomLeftRadius: innerRadius,
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    }),
    dropdownButton: css({
      label: 'assistant-split-button-dropdown',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0,
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
      borderTopRightRadius: innerRadius,
      borderBottomRightRadius: innerRadius,
      padding: '0 !important',
      boxShadow: `inset 1px 0 0 ${dividerColor}`,
      '& svg': {
        margin: 0,
      },
    }),
  };
};

function toCssWidth(width: number | string): string {
  return typeof width === 'number' ? `${width}px` : width;
}
